import { Modal, ModalBody } from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import IconTrash2 from "@/icons/IconTrash2";
import IconEditPencil2 from "@/icons/IconEditPencil2";
import IconCheckMark from "@/icons/IconCheckMark";
import { IconClose } from "@velaro/velaro-component-lib";
import { AlertTriangle } from "@velaro/velaro-component-lib";
import React, { useEffect, useRef, useState } from "react";
import { openAccelaAuthPopup } from "./AccelaAuthPopup";
import { PrimaryBlueButton } from "@velaro/velaro-component-lib";
import { PrimaryGrayButton } from "@velaro/velaro-component-lib";
import { IconPlus } from "@velaro/velaro-component-lib/";
import { useNavigate } from "react-router-dom";

interface AccelaConfig {
  id: number;
  agencyName: string;
  clientId: string;
  clientSecret?: string;
  emailAddress: string;
  teamId: number;
  scope?: string;
  refreshTokenExpired?: boolean;
  token?: string;
}

interface Props {
  configs: AccelaConfig[];
  deleteConfig(id: number): void;
  addConfig(): void;
  teamId: number;
  onChange(id: number, field: keyof AccelaConfig, value: string): void;
  onSelectConfig(id: number): void;
  updateConfig(id: number, updatedConfig: Partial<AccelaConfig>): void;
  refreshConfigs?: () => Promise<void>;
}

export default function AccelaAccounts(props: Props) {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [editForm, setEditForm] = useState({
    id: 0,
    clientId: "",
    agencyName: "",
    teamId: 0,
    clientSecret: "",
    emailAddress: "",
    environment: "TEST",
    scope: "",
    redirectUri: ""
  });

  const openEditModal = (config: AccelaConfig) => {
    setEditForm({
      ...config,
      clientSecret: config.clientSecret || "",
      emailAddress: config.emailAddress || "",
      environment: "TEST",
      scope:
        "records addresses contacts inspections owners parcels documents workflows conditions professionals global_search offline_access",
      redirectUri:
        "https://localhost:9000/integrations/accela-civic/AccelaCallbackBridge"
    });
    setEditModalOpen(true);
  };

  const handleEditFormChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveConfig = async () => {
    try {
      await api.messaging.post(`Accela/Auth/SaveCredentials`, {
        teamId: editForm.teamId,
        clientId: editForm.clientId,
        clientSecret: editForm.clientSecret,
        agencyName: editForm.agencyName,
        emailAddress: editForm.emailAddress,
        accelaBaseUrl: "https://apis.accela.com/v4",
        scope: editForm.scope
      });
      props.onChange(editForm.id, "agencyName", editForm.agencyName);
      setEditModalOpen(false);
      setEditingId(null);

      if (typeof props.refreshConfigs === "function") {
        await props.refreshConfigs();
      }
    } catch (error) {
      console.error("Failed to update account", error);
    }
  };
  const api = useApi();
  const navigate = useNavigate();
  const [selectedConfigId, setSelectedConfigId] = useState(
    props.configs.length === 1 ? props.configs[0].id : 0
  );
  const [selectedConfig, setSelectedConfig] = useState<AccelaConfig | null>(
    props.configs.length > 0 ? props.configs[0] : null
  );

  const [isOpen, setIsOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<{
    id: number;
    agencyName: string;
  } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (props.configs.length > 0) {
      const lastConfig = props.configs[props.configs.length - 1];
      setSelectedConfigId(lastConfig.id);
      setSelectedConfig(lastConfig);
      props.onSelectConfig(lastConfig.id);
    } else {
      setSelectedConfig(null);
    }
  }, [props.configs]);

  const handleDeleteClick = (id: number, agencyName: string) => {
    setConfigToDelete({ id, agencyName });
    setIsModalOpen(true);
  };

  const confirmDelete = async (id?: number) => {
    if (!id) return;
    await api.messaging.delete(`NetSuite/Integrations/${id}`);
    props.deleteConfig(id);
    setIsModalOpen(false);
    setConfigToDelete(null);
  };

  const updateConfig = async (
    id: number,
    updatedConfig: Partial<AccelaConfig>
  ) => {
    try {
      await api.messaging.post(`Accela/Integrations/${id}`, updatedConfig);
      props.updateConfig(id, updatedConfig);
    } catch (error) {
      console.error("Failed to update config", error);
    }
  };

  const handleAccelaAuth = async (config: AccelaConfig) => {
    openAccelaAuthPopup({
      clientId: config.clientId,
      agencyName: config.agencyName,
      environment: "",
      scope:
        "records addresses contacts inspections owners parcels documents workflows conditions professionals global_search offline_access",
      onAuthComplete: async ({ code, state }) => {
        const exchangeResp = await api.messaging.post(
          "Accela/Auth/ExchangeCode",
          {
            code,
            state: state || "",
            clientId: config.clientId
          }
        );
        if (exchangeResp.ok) {
          if (typeof props.refreshConfigs === "function") {
            await props.refreshConfigs();
          }
        } else {
          console.error("Token exchange failed");
        }
      },
      onError: (err) => {
        if (err && err.message === "Popup closed by user") {
          window.location.href = "/integrations/accela-civic";
        } else {
          console.error("Accela popup error:", err);
        }
      }
    });
  };

  const handleSelect = (config: AccelaConfig) => {
    if (editingId !== config.id) {
      setSelectedConfig(config);
      setSelectedConfigId(config.id);
      props.onSelectConfig(config.id);
      setIsOpen(false);
    }
  };

  const handleSave = async (config: AccelaConfig) => {
    try {
      await api.messaging.post(
        `Accela/Auth/UpdateEmail/${config.id}`,
        {
          emailAddress: editValue
        }
      );
      props.onChange(config.id, "emailAddress", editValue);
      setEditingId(null);
    } catch (error) {
      console.error("Failed to update email address", error);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
  };

  const handleEdit = (config: AccelaConfig) => {
    setEditingId(config.id);
    setEditValue(config.emailAddress);
  };

  return (
    <div className="panel bg-white dark:bg-stone-300 rounded-2xl shadow-md mb-4 p-6">
      <table className="table-fixed w-full p-4">
        <thead>
          <tr>
            <th className="text-slate-800 text-lg font-medium leading-7 text-left pr-6">
              Accounts
            </th>
            <th className="text-slate-800 text-sm font-medium text-left pr-6"></th>
            <th className="text-slate-800 text-sm font-medium text-left pr-6"></th>
            <th className="text-slate-800 text-sm font-medium text-left pr-6"></th>
            <th className="text-right w-48">
              <PrimaryBlueButton
                label="Add Account"
                onClick={() =>
                  navigate(`/integrations/accela-civic/accelacreate`, {
                    state: { teamId: props.teamId }
                  })
                }
                prefixIcon={React.createElement(IconPlus)}
                className="w-48 h-full font-medium"
              />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th className="text-slate-800 text-sm font-medium text-left pr-6">
              Email Address
            </th>
            <th className="text-slate-800 text-sm font-medium text-left pr-6">
              Agency Name
            </th>
            <th className="text-slate-800 text-sm font-medium text-left pr-6">
              Client ID
            </th>
            <th className="text-slate-800 text-sm font-medium text-left pr-6">
              Token Status
            </th>
          </tr>
          <tr>
            <td className="pr-6 w-full">
              {props.configs.length > 1 ? (
                <div ref={dropdownRef} className="relative w-full">
                  <button
                    className="w-full text-sm font-normal bg-white border border-gray-300 rounded-lg px-4 py-2 text-left focus:ring-1 focus:ring-[#1670e7] focus:border-[#1670e7]"
                    onClick={() => setIsOpen(!isOpen)}
                  >
                    {props.configs.find(
                      (config) => config.id === selectedConfigId
                    )?.emailAddress || "Select an option"}
                  </button>
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <i
                      className={`transition fa-solid ${
                        isOpen ? "fa-angle-up" : "fa-angle-down"
                      } text-slate-400 mr-1`}
                    ></i>
                  </div>
                  {isOpen && (
                    <ul className="absolute w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 p-2">
                      {props.configs.map((config) => (
                        <li
                          key={config.id}
                          className={`flex justify-between items-center rounded-lg px-3 py-3 hover:bg-slate-100 
                            text-sm font-normal hover:text-slate-800 hover:rounded-lg cursor-pointer group gap-2 mb-2
                            ${
                              config.id === selectedConfigId
                                ? "text-[#3383ec] bg-slate-100"
                                : "text-slate-800"
                            }`}
                          onClick={() => handleSelect(config)}
                        >
                          {editingId === config.id ? (
                            <>
                              <input
                                type="text"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                className="w-full px-3 py-3 border rounded-lg"
                              />
                              <button onClick={handleCancel}>
                                <IconClose className="w-8 text-slate-500" />
                              </button>
                              <button onClick={() => handleSave(config)}>
                                <IconCheckMark className="w-8 text-slate-500" />
                              </button>
                            </>
                          ) : (
                            <span>{config.emailAddress}</span>
                          )}

                          {editingId !== config.id && (
                            <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEdit(config);
                                }}
                              >
                                <IconEditPencil2 className="w-8 text-slate-800" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (selectedConfig) {
                                    handleDeleteClick(
                                      config.id,
                                      config.emailAddress
                                    );
                                  }
                                }}
                              >
                                <IconTrash2 className="w-8 text-slate-800" />
                              </button>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                <input
                  type="text"
                  value={selectedConfig?.emailAddress || ""}
                  onChange={(e) =>
                    props.onChange(
                      selectedConfigId,
                      "emailAddress",
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                />
              )}
            </td>
            <td className="pr-6 w-full">
              <span className="text-slate-800 text-sm font-normal">
                {selectedConfig?.agencyName || ""}
              </span>
            </td>
            <td className="pr-6 w-full">
              <span className="text-slate-800 text-sm font-normal">
                {selectedConfig?.clientId}
              </span>
            </td>
            <td className="pr-6 w-full">
              {!selectedConfig?.token ? (
                <span className="flex items-center gap-2 text-yellow-600 text-sm font-semibold">
                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                  Not yet authenticated.{" "}
                  <button
                    className="underline text-blue-600"
                    onClick={() =>
                      handleAccelaAuth(
                        selectedConfig ? selectedConfig : props.configs[0]
                      )
                    }
                  >
                    Authenticate
                  </button>
                </span>
              ) : selectedConfig?.refreshTokenExpired ? (
                <span className="flex items-center gap-2 text-red-600 text-sm font-semibold">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  Token expired.{" "}
                  <button
                    className="underline text-blue-600"
                    onClick={() => {
                      if (selectedConfig) handleAccelaAuth(selectedConfig);
                    }}
                  >
                    Re-authenticate
                  </button>
                </span>
              ) : (
                <span className="text-green-600 text-sm font-semibold flex items-center gap-1">
                  <IconCheckMark className="w-4 h-4" />
                  Active
                </span>
              )}
            </td>
            <td className="text-right w-48">
              <PrimaryGrayButton
                label="Saved"
                onClick={() => {
                  if (selectedConfig) openEditModal(selectedConfig);
                }}
                className="w-full h-full disabled"
              />
            </td>
            {/* <td className="pr-6 w-full">
              <button
                onClick={() => {
                  if (selectedConfig) openEditModal(selectedConfig);
                }}
              >
                <IconEditPencil2 className="w-8 text-slate-800" />
              </button>
              <button
                onClick={() => {
                  if (selectedConfig?.agencyName) {
                    handleDeleteClick(
                      selectedConfigId,
                      selectedConfig.agencyName
                    );
                  }
                }}
              >
                <IconTrash2 className="w-8 text-slate-800" />
              </button>
            </td> */}
          </tr>
        </tbody>
      </table>

      {editModalOpen && (
        <Modal show={editModalOpen}>
          <ModalBody>
            <div className="flex items-center mb-6 justify-between">
              <div className="text-slate-800 text-xl font-medium">
                Edit Account
              </div>
              <button onClick={() => setEditModalOpen(false)}>
                <IconClose className="w-8 text-slate-500" />
              </button>
            </div>
            <form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Email Address
                </label>
                <input
                  type="email"
                  name="emailAddress"
                  value={editForm.emailAddress}
                  onChange={handleEditFormChange}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Client ID
                </label>
                <input
                  type="text"
                  name="clientId"
                  value={editForm.clientId}
                  onChange={handleEditFormChange}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Agency Name
                </label>
                <input
                  type="text"
                  name="agencyName"
                  value={editForm.agencyName}
                  onChange={handleEditFormChange}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Client Secret
                </label>
                <input
                  type="text"
                  name="clientSecret"
                  value={editForm.clientSecret}
                  onChange={handleEditFormChange}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Environment
                </label>
                <select
                  name="environment"
                  value={editForm.environment}
                  onChange={handleEditFormChange}
                  className="w-full px-4 py-2 border rounded-lg"
                >
                  <option value="TEST">TEST</option>
                  <option value="PROD">PROD</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Scope
                </label>
                <textarea
                  name="scope"
                  value={editForm.scope}
                  onChange={handleEditFormChange}
                  className="w-full px-4 py-2 border rounded-lg"
                  rows={3}
                />
              </div>
              <div className="pt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setEditModalOpen(false)}
                  className="rounded-lg px-6 py-2 bg-white border border-cornflower-blue-500 hover:border-cornflower-blue-400 text-[#1670e7] text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveConfig}
                  className="rounded-lg px-6 py-2 bg-cornflower-blue-500 border border-cornflower-blue-500 hover:bg-cornflower-blue-400 hover:border-cornflower-blue-400 text-white text-sm font-medium"
                >
                  Save
                </button>
              </div>
            </form>
          </ModalBody>
        </Modal>
      )}
      {isModalOpen && (
        <Modal show={isModalOpen}>
          <ModalBody>
            <div className="flex items-center mb-6 justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-8  text-[#EE3763]" />
                <div className="text-slate-800 text-xl font-medium">
                  Remove Account
                </div>
              </div>
              <button onClick={() => setIsModalOpen(false)}>
                <IconClose className="w-8 text-slate-500" />
              </button>
            </div>
            <div className="text-center text-slate-800 text-sm font-normal">
              Are you sure you want to remove the account{" "}
              {configToDelete?.agencyName}? This action cannot be undone.
            </div>
            <div className="pt-6 flex justify-center gap-4">
              <button
                onClick={() => setIsModalOpen(false)}
                className="rounded-lg px-6 py-2 bg-white border border-cornflower-blue-500 hover:border-cornflower-blue-400 text-[#1670e7] text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => confirmDelete(configToDelete?.id)}
                className="rounded-lg px-6 py-2 bg-cornflower-blue-500 border border-cornflower-blue-500 hover:bg-cornflower-blue-400 hover:border-cornflower-blue-400 text-white text-sm font-medium"
              >
                Remove
              </button>
            </div>
          </ModalBody>
        </Modal>
      )}
    </div>
  );
}
